import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    // Galore: iOS Safari crashes with "A problem repeatedly occurred" when the
    // mobile grid-density toggle fires View Transitions in quick succession on
    // collection pages — each transition snapshots every visible card, and
    // mobile WebKit runs out of memory after a couple of toggles. On mobile
    // (where the toggle is just 1-up vs 2-up), skip the animation and apply
    // the layout change instantly. Desktop keeps the animated transition.
    if (!mediaQueryLarge.matches) {
      this.#setLayout(value);
    } else {
      await startViewTransition(() => this.#setLayout(value), ['product-grid']);
    }

    // Galore: lazy product images that reflow into view via the density toggle
    // (with no scroll event) are never re-evaluated by the browser's native
    // lazy-loading, so they stay deferred and blank — the reported "switch from
    // single back to grid doesn't load". Wake the now-visible ones after the
    // layout settles. requestAnimationFrame ensures we measure the NEW layout.
    requestAnimationFrame(() => this.#wakeVisibleImages());

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Forces lazy images that just reflowed into view (via the density toggle, not
   * a scroll) to load. Native lazy-loading only re-evaluates on scroll, so images
   * pulled into the viewport by the toggle stay deferred and blank. We force
   * loading="eager" on the now-visible ones and pin sizes="auto" to the real
   * rendered width — sizes="auto" never resolves a srcset candidate for an image
   * the browser already deferred, so without it the image would still load nothing.
   * Only touches in/near-viewport, not-yet-loaded images, so it never bulk-loads
   * the whole grid.
   */
  #wakeVisibleImages() {
    const { grid } = this.refs;
    if (!grid) return;

    const viewportHeight = window.innerHeight;
    const topLimit = viewportHeight * 1.5;
    const bottomLimit = -viewportHeight * 0.5;

    for (const image of grid.querySelectorAll('img')) {
      if (image.complete && image.naturalWidth > 0) continue;

      const rect = image.getBoundingClientRect();
      if (rect.width === 0) continue; // hidden slide
      if (rect.top > topLimit || rect.bottom < bottomLimit) continue; // far off-screen

      if ((image.getAttribute('sizes') || '').includes('auto')) {
        image.setAttribute('sizes', `${Math.round(rect.width)}px`);
      }
      if (image.getAttribute('loading') === 'lazy') {
        image.setAttribute('loading', 'eager');
      }
    }
  }

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
